import type { ChromeExtensionSelectionMode } from '@prisma/client';
import { Button, Select } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import Link from 'next/link';
import { useState, useMemo } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { CHROME_EXTENSION_SELECTION_MODE_OPTIONS } from '~/common/chrome-extensions/constants';

export const ExtensionPicker = ({ testContentId }: { testContentId: string }) => {
  const [selectedExtension, setSelectedExtension] = useState<string>();
  const [selectedVersion, setSelectedVersion] = useState<string>();
  const [mode, setMode] = useState<ChromeExtensionSelectionMode>('PINNED');
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();

  const { data: extensions = [] } = api.chromeExtension.list.useQuery({ projectId: projectId ?? '' });
  const { mutateAsync: addExtensionToTest, isPending } = api.testContent.addChromeExtension.useMutation({
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to add the extension to the test.',
        color: 'red',
      });
    },
    onSuccess: async () => {
      await trpcUtils.testContent.get.invalidate({ id: testContentId }).catch(() => undefined);
      modals.closeAll();
    },
  });

  const availableVersions = useMemo(
    () => extensions.find((ext) => ext.id === selectedExtension)?.versions || [],
    [extensions, selectedExtension],
  );

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">Select a Chrome extension and version to install on your virtual browser.</p>

      <div className="flex w-full flex-col gap-2">
        <Select
          label="Extension"
          variant="filled"
          radius="md"
          placeholder="Select extension"
          value={selectedExtension}
          onChange={(value) => {
            if (value) {
              setSelectedExtension(value);
              setSelectedVersion(undefined);
            }
          }}
          data={extensions.map((extension) => ({ value: extension.id, label: extension.name }))}
        />

        <div className="grid w-full gap-2 md:grid-cols-2">
          <Select
            label="Mode"
            variant="filled"
            radius="md"
            value={mode}
            onChange={(value) => {
              // It will be either LATEST or PINNED, since it comes from the select options.
              const newMode = value === 'LATEST' ? 'LATEST' : 'PINNED';
              setMode(newMode);
              if (newMode === 'LATEST') {
                setSelectedVersion(undefined);
              }
            }}
            data={CHROME_EXTENSION_SELECTION_MODE_OPTIONS}
            placeholder="Select mode"
            disabled={!selectedExtension}
          />
          <Select
            label="Version"
            variant="filled"
            radius="md"
            key={selectedVersion}
            placeholder={selectedExtension ? 'Select version' : 'Select an extension first'}
            value={selectedVersion}
            onChange={(val) => {
              if (val) {
                setSelectedVersion(val);
              }
            }}
            data={availableVersions.map((version) => ({ value: version.id, label: version.version }))}
            disabled={!selectedExtension || mode === 'LATEST'}
          />
        </div>
      </div>
      <Link
        href={`/project/${projectId}/testData?tab=chromeExtension`}
        target="_blank"
      >
        <p className="text-muted-foreground text-xs transition-all hover:text-blue-500">
          Don&apos;t see the extension you&apos;re looking for? Upload a new one
        </p>
      </Link>
      <div className="mt-5 w-full">
        <Button
          className="w-full"
          onClick={async () => {
            if (selectedExtension && (mode === 'LATEST' || selectedVersion)) {
              await addExtensionToTest({
                testContentId,
                chromeExtensionId: selectedExtension,
                mode,
                versionId: mode === 'PINNED' ? selectedVersion : undefined,
              });
            }
          }}
          disabled={!selectedExtension || (mode === 'PINNED' && !selectedVersion) || isPending}
        >
          {isPending ? 'Installing...' : 'Install'}
        </Button>
      </div>
    </div>
  );
};
